import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { questionnaireService } from '../services/questionnaireService';

const QuestionnaireList = () => {
  const [questionnaires, setQuestionnaires] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [questionnaireToDelete, setQuestionnaireToDelete] = useState(null);
  const [questionnaireToAssign, setQuestionnaireToAssign] = useState(null);
  const [assignLoading, setAssignLoading] = useState(false);
  const [notification, setNotification] = useState(null);
  const navigate = useNavigate();

  // Notification functions
  const showNotification = (message, type) => {
    setNotification({ message, type });
    setTimeout(() => {
      setNotification(null);
    }, 5000);
  };

  useEffect(() => {
    const fetchQuestionnaires = async () => {
      try {
        setLoading(true);
        const data = await questionnaireService.fetchQuestionnaires();
        setQuestionnaires(data);
        setError(null);
      } catch (err) {
        setError('Failed to fetch Quizes. Please try again later.');
        console.error('Error fetching Quizes:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchQuestionnaires();
  }, []);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const filteredQuestionnaires = questionnaires.filter(questionnaire => 
    questionnaire.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (questionnaire.description && questionnaire.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentQuestionnaires = filteredQuestionnaires.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredQuestionnaires.length / itemsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const confirmDelete = (questionnaire) => {
    setQuestionnaireToDelete(questionnaire);
    setShowDeleteModal(true);
  };

  const confirmAssign = (questionnaire) => {
    setQuestionnaireToAssign(questionnaire);
    setShowAssignModal(true);
  };

  const handleDelete = async () => {
    if (!questionnaireToDelete) return;
    
    try {
      setLoading(true);
      await questionnaireService.deleteQuestionnaire(questionnaireToDelete.id);
      setQuestionnaires(questionnaires.filter(q => q.id !== questionnaireToDelete.id));
      setShowDeleteModal(false);
      setQuestionnaireToDelete(null);
      showNotification('Quiz deleted successfully!', 'success');
    } catch (err) {
      showNotification('Failed to delete Quiz. Please try again.', 'error');
      console.error('Error deleting questionnaire:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAssign = async () => {
    if (!questionnaireToAssign) return;
    
    try {
      setAssignLoading(true);
      await questionnaireService.assignQuestionnaire(questionnaireToAssign.id);
      setShowAssignModal(false);
      setQuestionnaireToAssign(null);
      showNotification('Quiz assigned successfully!', 'success');
    } catch (err) {
      showNotification('Failed to assign questionnaire. Please try again.', 'error');
      console.error('Error assigning Quizes:', err);
    } finally {
      setAssignLoading(false);
    }
  };

  const handleEdit = (id) => {
    navigate(`/questionnaires/edit/${id}`);
  };

  const handleView = (id) => {
    navigate(`/questionnaires/${id}`);
  };

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  return (
    <div className="container">
      {/* Notification component */}
      {notification && (
        <div className={`notification ${notification.type}`}>
          <span>{notification.message}</span>
          <button 
            className="notification-close"
            onClick={() => setNotification(null)}
          >
            ×
          </button>
        </div>
      )}

      <div className="header">
        <h1>Quizes</h1>
        <p className="subtitle">Manage your Quizes</p>
        <Link to="/questionnaires/create" className="create-button">
          Create New Quiz
        </Link>
      </div>

      <div className="search-container">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search questionnaires..."
            value={searchTerm}
            onChange={handleSearch}
            className="search-input"
          />
        </div>
        <div className="results-count">
          Showing {currentQuestionnaires.length} of {filteredQuestionnaires.length} Quizes
        </div>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {loading && (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading...</p>
        </div>
      )}

      {!loading && currentQuestionnaires.length === 0 ? (
        <div className="empty-state">
          <p>No Quizes found.</p>
          {searchTerm && (
            <p>
              Try adjusting your search or{' '}
              <Link to="/questionnaires/create">create a new Quiz</Link>.
            </p>
          )}
          {!searchTerm && (
            <p>
              <Link to="/questionnaires/create">Create your first Quiz</Link> to get started.
            </p>
          )}
        </div>
      ) : (
        <>
          <table className="questionnaires-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Description</th>
                <th>Questions</th>
                <th>Created</th>
                <th>Last Updated</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentQuestionnaires.map((questionnaire) => (
                <tr key={questionnaire.id}>
                  <td className="title-cell">{questionnaire.title}</td>
                  <td>
                    {questionnaire.description ? (
                      questionnaire.description.length > 50 
                        ? `${questionnaire.description.substring(0, 50)}...` 
                        : questionnaire.description
                    ) : (
                      <span className="no-description">No description</span>
                    )}
                  </td>
                  <td>
                    <span className="badge">
                      {questionnaire._count.questions} questions
                    </span>
                  </td>
                  <td>{formatDate(questionnaire.createdAt)}</td>
                  <td>{formatDate(questionnaire.updatedAt)}</td>
                  <td>
                    <div className="action-buttons">
   
                      <button 
                        className="edit-button"
                        onClick={() => handleEdit(questionnaire.id)}
                        title="Edit questionnaire"
                      >
                        Edit
                      </button>
                      <button 
                        className="assign-button"
                        onClick={() => confirmAssign(questionnaire)}
                        title="Assign questionnaire"
                      >
                        Assign
                      </button>
                      <button 
                        className="delete-button"
                        onClick={() => confirmDelete(questionnaire)}
                        title="Delete questionnaire"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {totalPages > 1 && (
            <div className="pagination">
              <button 
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="pagination-button"
              >
                Previous
              </button>
              
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(number => (
                <button
                  key={number}
                  onClick={() => handlePageChange(number)}
                  className={`pagination-button ${number === currentPage ? 'active' : ''}`}
                >
                  {number}
                </button>
              ))}
              
              <button 
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="pagination-button"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {/* Delete confirmation modal */}
      {showDeleteModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>Confirm Deletion</h2>
              <button 
                className="close-button"
                onClick={() => setShowDeleteModal(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              Are you sure you want to delete the questionnaire 
              <strong> "{questionnaireToDelete?.title}"</strong>? 
              This action cannot be undone.
            </div>
            <div className="modal-footer">
              <button 
                className="cancel-button"
                onClick={() => setShowDeleteModal(false)}
              >
                Cancel
              </button>
              <button 
                className="delete-button"
                onClick={handleDelete}
                disabled={loading}
              >
                {loading ? 'Deleting...' : 'Delete Questionnaire'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Assign confirmation modal */}
      {showAssignModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>Confirm Assignment</h2>
              <button 
                className="close-button"
                onClick={() => setShowAssignModal(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              Are you sure you want to assign the questionnaire 
              <strong> "{questionnaireToAssign?.title}"</strong>?
            </div>
            <div className="modal-footer">
              <button 
                className="cancel-button"
                onClick={() => setShowAssignModal(false)}
              >
                Cancel
              </button>
              <button 
                className="assign-button"
                onClick={handleAssign}
                disabled={assignLoading}
              >
                {assignLoading ? 'Assigning...' : 'Assign Questionnaire'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuestionnaireList;